import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CART_CACHE_KEY = 'my_carts_cache';
const ACTIVE_CART_STATUSES = ['active', 'pending_payment', 'processing_payment', 'pending_counter'];

const CartContext = createContext(null);

const parseCachedCarts = () => {
  try {
    const raw = localStorage.getItem(CART_CACHE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (error) {
    return [];
  }
};

const mergeCart = (carts, cart) => {
  const index = carts.findIndex((entry) => entry.id === cart.id);
  if (index === -1) return [cart, ...carts];
  const next = [...carts];
  next[index] = cart;
  return next;
};

export function CartProvider({ children }) {
  const [carts, setCarts] = useState(() => parseCachedCarts());
  const [loading, setLoading] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [itemMutating, setItemMutating] = useState({});
  const flushTimersRef = useRef({});
  const addQueueRef = useRef({});
  const mutationQueueRef = useRef({});
  const qtyPendingDeltaRef = useRef({});
  const qtyFlushTimersRef = useRef({});
  const itemPendingDeltaRef = useRef({});
  const mutationSeqRef = useRef(0);
  const latestAppliedSeqRef = useRef({});
  const cartsRef = useRef(carts);

  useEffect(() => {
    cartsRef.current = carts;
  }, [carts]);

  const setCartsState = useCallback((updater) => {
    setCarts((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      localStorage.setItem(CART_CACHE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const applyCartWrite = useCallback((updater) => {
    setCartsState(updater);
  }, [setCartsState]);

  const enqueueCartMutation = useCallback((cartId, task) => {
    const queueKey = cartId || 'active-cart';
    const previous = mutationQueueRef.current[queueKey] || Promise.resolve();
    const next = previous
      .catch(() => {})
      .then(task)
      .finally(() => {
        if (mutationQueueRef.current[queueKey] === next) delete mutationQueueRef.current[queueKey];
      });
    mutationQueueRef.current[queueKey] = next;
    return next;
  }, []);

  const refreshCarts = useCallback(async ({ silent = true } = {}) => {
    if (!silent) setLoading(true);
    try {
      const response = await axios.get(`${API}/carts/my`);
      setCartsState(response.data || []);
      return response.data || [];
    } catch (error) {
      if (!silent) toast.error('Could not refresh cart');
      throw error;
    } finally {
      if (!silent) setLoading(false);
    }
  }, [setCartsState]);

  useEffect(() => {
    refreshCarts({ silent: true })
      .catch(() => {})
      .finally(() => setHydrated(true));
  }, [refreshCarts]);

  useEffect(() => () => {
    Object.values(flushTimersRef.current).forEach((timerId) => clearTimeout(timerId));
    Object.values(qtyFlushTimersRef.current).forEach((timerId) => clearTimeout(timerId));
  }, []);

  const activeCart = useMemo(() => (
    carts.find((cart) => ACTIVE_CART_STATUSES.includes(cart.cart_status))
  ), [carts]);

  const resetCartLifecycle = useCallback(() => {
    Object.values(flushTimersRef.current).forEach((timerId) => clearTimeout(timerId));
    flushTimersRef.current = {};
    Object.values(qtyFlushTimersRef.current).forEach((timerId) => clearTimeout(timerId));
    qtyFlushTimersRef.current = {};
    qtyPendingDeltaRef.current = {};
    addQueueRef.current = {};
    itemPendingDeltaRef.current = {};
    mutationQueueRef.current = {};
    latestAppliedSeqRef.current = {};
    setItemMutating({});
    applyCartWrite((prev) => prev.filter((cart) => !ACTIVE_CART_STATUSES.includes(cart.cart_status)));
  }, [applyCartWrite]);

  const optimisticAdd = useCallback((item, quantity) => {
    const fallbackCart = {
      id: 'optimistic-cart',
      cart_status: 'active',
      items: [],
      total: 0,
      total_current_price: 0,
      total_actual_price: 0,
    };

    applyCartWrite((prev) => {
      const all = prev.length ? [...prev] : [fallbackCart];
      const cartIndex = all.findIndex((entry) => ACTIVE_CART_STATUSES.includes(entry.cart_status) || entry.id === 'optimistic-cart');
      if (cartIndex < 0) return all;

      const cart = {
        ...all[cartIndex],
        items: [...(all[cartIndex].items || [])],
      };
      const itemIndex = cart.items.findIndex((entry) => entry.item_id === item.id);
      if (itemIndex >= 0) {
        const line = cart.items[itemIndex];
        cart.items[itemIndex] = { ...line, quantity: Number(line.quantity || 0) + quantity };
      } else {
        cart.items.push({
          item_id: item.id,
          name: item.title,
          title: item.title,
          price: item.price,
          actual_price: item.actual_price || item.price,
          original_price: item.actual_price || item.price,
          image_url: item.image_url || item.image_data,
          quantity,
        });
      }

      const subtotal = cart.items.reduce((sum, line) => sum + Number(line.price || 0) * Number(line.quantity || 0), 0);
      const actualSubtotal = cart.items.reduce((sum, line) => sum + Number(line.actual_price ?? line.original_price ?? line.price ?? 0) * Number(line.quantity || 0), 0);
      cart.total_current_price = subtotal;
      cart.total_actual_price = actualSubtotal;
      cart.total_discount = Math.max(0, actualSubtotal - subtotal);
      cart.total = Math.max(0, subtotal - Number(cart.discount_amount || 0) - Number(cart.gift_card_amount || 0));

      all[cartIndex] = cart;
      return all;
    });
  }, []);

  const flushQueuedAdd = useCallback((itemId, itemMeta) => {
    const pendingDelta = itemPendingDeltaRef.current[itemId] || 0;
    if (!pendingDelta) return Promise.resolve();

    const active = cartsRef.current.find((entry) => ACTIVE_CART_STATUSES.includes(entry.cart_status));
    const queueCartId = active?.id || 'active-cart';

    return enqueueCartMutation(queueCartId, async () => {
      const quantity = itemPendingDeltaRef.current[itemId] || 0;
      if (!quantity) return;
      itemPendingDeltaRef.current[itemId] = 0;
      setItemMutating((prev) => ({ ...prev, [itemId]: true }));

      try {
        const { data } = await axios.post(`${API}/carts/items/add`, {
          item: {
            item_id: itemId,
            name: itemMeta.title,
            title: itemMeta.title,
            price: itemMeta.price,
            actual_price: itemMeta.actual_price || itemMeta.price,
            original_price: itemMeta.actual_price || itemMeta.price,
            image_url: itemMeta.image_url || itemMeta.image_data,
            quantity,
          },
        });
        applyCartWrite((prev) => mergeCart(prev, data));
      } catch (error) {
        itemPendingDeltaRef.current[itemId] = 0;
        await refreshCarts({ silent: true });
        toast.error('Failed to add item. Cart re-synced.');
      } finally {
        setItemMutating((prev) => ({ ...prev, [itemId]: false }));
      }
    });
  }, [applyCartWrite, enqueueCartMutation, refreshCarts]);

  const addItem = useCallback((item, quantity = 1) => {
    optimisticAdd(item, quantity);
    addQueueRef.current[item.id] = (addQueueRef.current[item.id] || 0) + quantity;
    itemPendingDeltaRef.current[item.id] = (itemPendingDeltaRef.current[item.id] || 0) + quantity;

    if (flushTimersRef.current[item.id]) clearTimeout(flushTimersRef.current[item.id]);
    flushTimersRef.current[item.id] = setTimeout(() => {
      delete flushTimersRef.current[item.id];
      flushQueuedAdd(item.id, item);
    }, 40);

    return new Promise((resolve) => {
      setTimeout(() => {
        flushQueuedAdd(item.id, item)
          .finally(resolve);
      }, 45);
    });
  }, [flushQueuedAdd, optimisticAdd]);

  const flushPendingAdds = useCallback(async () => {
    const pendingEntries = Object.entries(itemPendingDeltaRef.current)
      .filter(([, quantity]) => Number(quantity) > 0);

    await Promise.all(pendingEntries.map(([itemId]) => {
      const active = (cartsRef.current || []).find((entry) => ACTIVE_CART_STATUSES.includes(entry.cart_status));
      const line = (active?.items || []).find((entry) => String(entry.item_id) === String(itemId));
      const itemMeta = {
        id: itemId,
        title: line?.title || line?.name || 'Item',
        price: Number(line?.price || 0),
        actual_price: Number(line?.actual_price ?? line?.original_price ?? line?.price ?? 0),
        image_url: line?.image_url,
      };
      return flushQueuedAdd(itemId, itemMeta);
    }));
  }, [flushQueuedAdd]);

  const updateItemQuantity = useCallback((cartId, itemId, _delta) => enqueueCartMutation(cartId, async () => {
    const queuedDelta = qtyPendingDeltaRef.current[itemId] || 0;
    if (!queuedDelta) return;
    qtyPendingDeltaRef.current[itemId] = 0;
    const sequence = ++mutationSeqRef.current;
    latestAppliedSeqRef.current[itemId] = Math.max(latestAppliedSeqRef.current[itemId] || 0, sequence);
    setItemMutating((prev) => ({ ...prev, [itemId]: true }));
    try {
      const { data } = await axios.put(`${API}/carts/${cartId}`, { item_id: itemId, delta: queuedDelta });
      if (sequence >= (latestAppliedSeqRef.current[itemId] || 0)) {
        latestAppliedSeqRef.current[itemId] = sequence;
        applyCartWrite((prev) => mergeCart(prev, data));
      }
    } catch (error) {
      qtyPendingDeltaRef.current[itemId] = 0;
      await refreshCarts({ silent: true });
      throw error;
    } finally {
      setItemMutating((prev) => ({ ...prev, [itemId]: false }));
    }
  }), [applyCartWrite, enqueueCartMutation, refreshCarts]);

  const queueQuantityUpdate = useCallback((cartId, itemId, delta) => {
    qtyPendingDeltaRef.current[itemId] = (qtyPendingDeltaRef.current[itemId] || 0) + delta;

    applyCartWrite((snapshot) => snapshot.map((cart) => {
      if (cart.id !== cartId) return cart;
      const nextItems = (cart.items || [])
        .map((item) => item.item_id === itemId ? { ...item, quantity: Number(item.quantity || 0) + delta } : item)
        .filter((item) => item.quantity > 0);
      const subtotal = nextItems.reduce((sum, line) => sum + Number(line.price || 0) * Number(line.quantity || 0), 0);
      const actualSubtotal = nextItems.reduce((sum, line) => sum + Number(line.actual_price ?? line.original_price ?? line.price ?? 0) * Number(line.quantity || 0), 0);
      return {
        ...cart,
        items: nextItems,
        total_current_price: subtotal,
        total_actual_price: actualSubtotal,
        total_discount: Math.max(0, actualSubtotal - subtotal),
        total: Math.max(0, subtotal - Number(cart.discount_amount || 0) - Number(cart.gift_card_amount || 0)),
      };
    }));

    if (qtyFlushTimersRef.current[itemId]) clearTimeout(qtyFlushTimersRef.current[itemId]);
    qtyFlushTimersRef.current[itemId] = setTimeout(() => {
      delete qtyFlushTimersRef.current[itemId];
      updateItemQuantity(cartId, itemId, delta).catch(() => {});
    }, 120);
  }, [applyCartWrite, updateItemQuantity]);

  const value = useMemo(() => ({
    carts,
    activeCart,
    loading,
    hydrated,
    refreshCarts,
    addItem,
    flushPendingAdds,
    updateItemQuantity: queueQuantityUpdate,
    resetCartLifecycle,
    setCarts: applyCartWrite,
    itemMutating,
  }), [carts, activeCart, loading, hydrated, refreshCarts, addItem, flushPendingAdds, updateItemQuantity, resetCartLifecycle, applyCartWrite, itemMutating]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
}
